import React, { useEffect, useState } from 'react';
import { AttendanceResponse } from '../../interfaces/attendance.interface';
import { AttendanceService } from '../../services/attendance.service';
import { Box, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, Grid, TextField } from '@mui/material';
import { format, isWithinInterval, parseISO, eachDayOfInterval, startOfDay, endOfDay, subDays } from 'date-fns';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import { LeaveApplicationResponse, LeaveStatus } from '../../interfaces/leave.interface';
import { LeaveService } from '../../services/leave.service';
import { HolidayResponse } from '../../interfaces/holiday.interface';
import { HolidayService } from '../../services/holiday.service';
import { useToast } from '../../context/ToastContext';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { AuthService } from '../../services/auth.service';
import { useTranslation } from 'react-i18next';
import { InstanceStatus, isInstanceStatus } from '../shifts/instanceStatus';

const JS_DAY_TO_NAME: WeekDay[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface DayStatus {
    kind: 'present' | 'absent' | 'holiday' | 'leave';
    label: string;
}

const AttendanceList: React.FC = () => {
    const [attendanceData, setAttendanceData] = useState<AttendanceResponse | null>(null);
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [holidayData, setHolidayData] = useState<HolidayResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasAttendanceToday, setHasAttendanceToday] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [attendanceButtonEnabled, setAttendanceButtonEnabled] = useState(true);
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(['FRIDAY', 'SATURDAY']);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const toast = useToast();
    const { t } = useTranslation(['attendance', 'common', 'shifts']);
    const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30)); // Default to 30 days ago
    const [endDate, setEndDate] = useState<Date>(new Date()); // Default to today
    const [dateRange, setDateRange] = useState<string[]>([]);
    const { shifts } = useShiftLookup();

    useEffect(() => {
        fetchData();
    }, [startDate, endDate]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const institutionId = AuthService.getSelectedInstitution();
                if (!institutionId) return;
                const settings = await InstitutionSettingsService.getSettings(institutionId);
                if (settings) {
                    setAttendanceButtonEnabled(settings.isAttendanceButtonEnabled);
                    if (settings.weekendDays && settings.weekendDays.length > 0) {
                        setWeekendDays(settings.weekendDays);
                    }
                }
            } catch {
                // Settings may not exist yet, default to enabled
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            // Generate array of all dates in range
            const range = eachDayOfInterval({
                start: startOfDay(startDate),
                end: endOfDay(endDate)
            });

            // Format dates to match the format used in attendances
            const formattedDates = range.map(date => format(date, 'yyyy-MM-dd'));
            setDateRange(formattedDates.reverse()); // Most recent dates first
        }
    }, [startDate, endDate]);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Fetch attendance data with date filters
            const { data, hasTodayAttendance } = await AttendanceService.getLoggedInUserAttendanceList(startDate, endDate);
            setAttendanceData(data);
            setHasAttendanceToday(hasTodayAttendance);

            // Fetch leave data
            const leaveResponse = await LeaveService.getLeaveApplications(0, 100);
            setLeaveData(leaveResponse);

            // Fetch holiday data
            const holidayResponse = await HolidayService.getHolidaysByInstitution();
            setHolidayData(holidayResponse);
        } catch (err) {
            toast.showApiError(err, t('fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCheckIn = async () => {
        try {
            setSubmitting(true);
            await AttendanceService.clockIn();
            await fetchData();
        } catch (err) {
            toast.showApiError(err, t('attendanceList.checkInFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleCheckOut = async () => {
        try {
            setSubmitting(true);
            await AttendanceService.clockOut();
            await fetchData();
        } catch (err) {
            toast.showApiError(err, t('attendanceList.checkOutFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const formatTime = (dateString: string) => {
        if (!dateString) return '-';
        return format(parseISO(dateString), 'HH:mm');
    };

    const formatDuration = (startStr: string, endStr: string): string => {
        if (!startStr || !endStr) return '-';
        const diffMs = parseISO(endStr).getTime() - parseISO(startStr).getTime();
        if (diffMs <= 0) return '-';
        const totalMinutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return t('duration', { hours, minutes });
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const getStatus = (date: string): DayStatus => {
        const dayName = JS_DAY_TO_NAME[parseISO(date).getDay()];
        if (weekendDays.includes(dayName)) {
            return { kind: 'holiday', label: t('dayStatus.weekend') };
        }

        // Check if it's a regular attendance day
        if (attendanceData?.content.some(attendance => attendance.date === date)) {
            return { kind: 'present', label: t('dayStatus.present') };
        }

        // Check if it's a leave day
        const leaveApplication = leaveData?.content.find(leave =>
            leave.leaveStatus === LeaveStatus.Approved &&
            isWithinInterval(parseISO(date), {
                start: parseISO(leave.startDate),
                end: parseISO(leave.endDate)
            })
        );

        if (leaveApplication) {
            return { kind: 'leave', label: leaveApplication.leaveType };
        }

        // Check if it's a holiday
        const holiday = holidayData?.content.find(h =>
            isWithinInterval(parseISO(date), {
                start: parseISO(h.startDate),
                end: parseISO(h.endDate)
            })
        );

        if (holiday) {
            return { kind: 'holiday', label: t('dayStatus.holiday', { name: holiday.name }) };
        }

        // If no attendance, leave, or holiday, mark as Absent
        return { kind: 'absent', label: t('dayStatus.absent') };
    };

    const getAttendanceByDate = (date: string) => {
        return attendanceData?.content.find(attendance => attendance.date === date);
    };

    const getStatusCellStyle = (status: DayStatus['kind']) => {
        // Base style
        const baseStyle = {
            ...tableCellStyle
        };

        // Add background color based on status
        if (status === 'present') {
            return { ...baseStyle, backgroundColor: '#e6ffe6' }; // Light green
        } else if (status === 'absent') {
            return { ...baseStyle, backgroundColor: '#ffebeb' }; // Light red
        } else if (status === 'holiday') {
            return { ...baseStyle, backgroundColor: '#ffffcc' }; // Light yellow
        }

        return baseStyle;
    };

    if (loading) return <Typography>{t('loading')}</Typography>;
    if (!attendanceData) return <Typography>{t('attendanceList.noData')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <Box>
                    <Typography variant="h5">
                        {t('attendanceList.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('attendanceList.timezoneNote', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })}
                    </Typography>
                </Box>
                <Box>
                    {attendanceButtonEnabled && (
                        !hasAttendanceToday ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCheckIn}
                                disabled={submitting}
                                sx={{ marginRight: 1 }}
                            >
                                {t('attendanceList.checkIn')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleCheckOut}
                                disabled={submitting}
                            >
                                {t('attendanceList.checkOut')}
                            </Button>
                        )
                    )}
                </Box>
            </Box>

            {/* Date Filter Controls */}
            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                <Grid>
                    <TextField
                        fullWidth
                        label={t('startDate')}
                        type="date"
                        value={format(startDate, 'yyyy-MM-dd')}
                        onChange={(e) => setStartDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ gridColumn: 'span 4' }}
                    />
                </Grid>
                <Grid>
                    <TextField
                        fullWidth
                        label={t('endDate')}
                        type="date"
                        value={format(endDate, 'yyyy-MM-dd')}
                        onChange={(e) => setEndDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ gridColumn: 'span 4' }}
                    />
                </Grid>
                <Grid>
                    <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel id="shift-filter-label">{t('shiftFilter.label')}</InputLabel>
                        <Select
                            labelId="shift-filter-label"
                            value={selectedShiftId}
                            label={t('shiftFilter.label')}
                            onChange={(e) => setSelectedShiftId(e.target.value)}
                        >
                            <MenuItem value="">{t('shiftFilter.all')}</MenuItem>
                            {shifts.filter(s => s.isActive).map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.code} – {s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={fetchData}
                        sx={{ mt: { xs: 0, md: 1 } }}
                    >
                        {t('applyFilters')}
                    </Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={tableHeaderStyle}>{t('common:fields.date')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.day')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.startTime')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.endTime')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.totalTime')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('common:fields.status')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.shift')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.instanceStatus')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.late')}</TableCell>
                            <TableCell sx={tableHeaderStyle}>{t('columns.ot')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dateRange
                            .filter(date => {
                                if (!selectedShiftId) return true;
                                const attendance = getAttendanceByDate(date);
                                return attendance?.shiftId === selectedShiftId;
                            })
                            .map((date) => {
                                const attendance = getAttendanceByDate(date);
                                const status = getStatus(date);
                                return (
                                    <TableRow key={date}>
                                        <TableCell sx={tableCellStyle}>{formatDate(date)}</TableCell>
                                        <TableCell sx={tableCellStyle}>{t(`common:weekdays.${parseISO(date).getDay() as WeekdayIndex}`)}</TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance ? formatTime(attendance.startTime) : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance ? formatTime(attendance.endTime) : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance ? formatDuration(attendance.startTime, attendance.endTime) : '-'}
                                        </TableCell>
                                        <TableCell sx={getStatusCellStyle(status.kind)}>{status.label}</TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance?.shiftCode
                                                ? <Chip label={attendance.shiftCode} size="small" />
                                                : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {isInstanceStatus(attendance?.instanceStatus)
                                                ? t(`shifts:instanceStatus.${attendance!.instanceStatus as InstanceStatus}`)
                                                : attendance?.instanceStatus ?? '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance?.isLate
                                                ? <Chip label={t('minutesShort', { minutes: attendance.lateMinutes })} size="small" color="error" />
                                                : '-'}
                                        </TableCell>
                                        <TableCell sx={tableCellStyle}>
                                            {attendance?.overtimeMinutes && attendance.overtimeMinutes > 0
                                                ? <Chip label={t('minutesShort', { minutes: attendance.overtimeMinutes })} size="small" />
                                                : '-'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default AttendanceList;
